// 5단계 배송 진행바 — 시안 03 (얇은 세그먼트 레일 + 그라데이션 필 + 상태 툴팁).
// step(1-5) 입력. 취소·환불 등 미노출 케이스는 호출 측에서 step 없을 때 렌더 생략.
// i18n: mesh ``_DELIVERY_STEP_STATUS_CODES`` ↔ ``order_status.{code}`` 단일 정본.

import type { CSSProperties } from "react";
import { useLabel } from "../lib/labels";

/** mesh ``order.py`` ``_DELIVERY_STEP_STATUS_CODES`` 와 동기 — 진행바 단계별 order_status 키 */
export const DELIVERY_STEP_STATUS_CODES = [
  "pending",
  "preparing_for_delivery",
  "picking_for_delivery",
  "on_delivery",
  "delivery_completed",
] as const;

/** mesh ``_STATUS_LABELS`` 영어 원문 폴백 (i18n 미적재·labels 미주입 시) */
export const DELIVERY_STEP_LABELS = [
  "Order Placed",
  "Preparing for Delivery",
  "Picking for Delivery",
  "On Delivery",
  "Delivery Completed",
] as const;

const _TOTAL = DELIVERY_STEP_STATUS_CODES.length;

interface Props {
  step: number;
  compact?: boolean;
}

function _clampStep(step: number): number {
  return Math.min(Math.max(Math.round(step), 1), _TOTAL);
}

/** Figma SVG `>` 체브론 (viewBox 0 0 4.70703 6, 흰색 40%) */
const _CHEVRON_PATH = "M4.70703 3L1.5 6H0L3.29297 3L0 0H1.5L4.70703 3Z";
/** Figma SVG 배지 화살표 (viewBox 0 0 15 5) — CSS 에서 scaleY(-1) 로 위를 향하게 플립 */
const _ARROW_PATH =
  "M6.7 3.93333C7.1 4.46667 7.9 4.46667 8.3 3.93333L15 -5H0L6.7 3.93333Z";

function _tipStyle(step: number): CSSProperties {
  if (step >= _TOTAL) {
    return { left: "100%", transform: "translateX(-100%)" };
  }
  const pct = (step / _TOTAL) * 100;
  return { left: `${pct}%`, transform: "translateX(-50%)" };
}

export default function DeliveryStepBar({ step, compact = false }: Props) {
  const active = _clampStep(step);
  const statusCode = DELIVERY_STEP_STATUS_CODES[active - 1];
  const tipLabel = useLabel(statusCode, DELIVERY_STEP_LABELS[active - 1]);
  const fillPct = (active / _TOTAL) * 100;

  return (
    <div className={compact ? "ac-progress ac-progress--compact" : "ac-progress"}>
      <div
        className="ac-progress__rail"
        role="progressbar"
        aria-valuenow={active}
        aria-valuemin={1}
        aria-valuemax={_TOTAL}
        aria-label={tipLabel}
      >
        <div
          className={
            active >= _TOTAL
              ? "ac-progress__fill ac-progress__fill--complete"
              : "ac-progress__fill"
          }
          style={{ width: `${fillPct}%` }}
        />
        <div className="ac-progress__markers" aria-hidden>
          {Array.from({ length: _TOTAL - 1 }, (_, i) => (
            <svg
              key={i}
              className="ac-progress__chevron"
              style={{ left: `${((i + 1) / _TOTAL) * 100}%` }}
              viewBox="0 0 4.70703 6"
              aria-hidden="true"
            >
              <path d={_CHEVRON_PATH} fill="#fff" fillOpacity={0.4} />
            </svg>
          ))}
        </div>
      </div>
      <div
        className={
          active >= _TOTAL
            ? "ac-progress__tip ac-progress__tip--end"
            : "ac-progress__tip"
        }
        style={_tipStyle(active)}
      >
        <svg
          className="ac-progress__tip-arrow"
          viewBox="0 0 15 5"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d={_ARROW_PATH} fill="currentColor" />
        </svg>
        <span className="ac-progress__tip-label">{tipLabel}</span>
      </div>
    </div>
  );
}
